import { SubscriptionTypes } from 'src/dashboard/settings/billing';

export interface FeatureRestriction {
  feature: string;
  allowedPlans: SubscriptionTypes[];
  upgradeMessage?: string;
  upgradePlan?: SubscriptionTypes;
}

// Feature restrictions based on the new pricing table
export const FEATURE_RESTRICTIONS: Record<string, FeatureRestriction> = {
  // Journey Limits (but template-based creation is allowed for all plans)
  unlimited_journeys: {
    feature: 'unlimited_journeys',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow plan for unlimited journeys',
    upgradePlan: 'pro'
  },

  // Journey Creation (checks if user has reached their plan's journey limit)
  journey_creation: {
    feature: 'journey_creation',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'You have reached your journey limit. Upgrade to create more journeys.',
    upgradePlan: 'pro'
  },

  // Template-based Journey Creation (available on all plans)
  template_journey_creation: {
    feature: 'template_journey_creation',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Template-based journey creation available on all plans'
  },

  // Journey Modes (Pitch & Microsite)
  journey_modes: {
    feature: 'journey_modes',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Journey modes available on all plans'
  },

  // Journey Insights
  basic_insights: {
    feature: 'basic_insights',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Basic insights available on all plans'
  },

  advanced_insights: {
    feature: 'advanced_insights',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for advanced insights',
    upgradePlan: 'enterprise'
  },

  // Session Replay
  session_replay: {
    feature: 'session_replay',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for session replay',
    upgradePlan: 'enterprise'
  },

  // Content Blocks
  content_blocks: {
    feature: 'content_blocks',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Unlimited content blocks available on all plans'
  },

  // Content Library
  content_library: {
    feature: 'content_library',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Content library available on all plans'
  },

  // Synced Steps
  synced_steps: {
    feature: 'synced_steps',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Synced steps available on all plans'
  },

  // Notifications
  email_notifications: {
    feature: 'email_notifications',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Email notifications available on all plans'
  },

  slack_notifications: {
    feature: 'slack_notifications',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for Slack notifications',
    upgradePlan: 'enterprise'
  },

  // Comments
  comments: {
    feature: 'comments',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Comments available on all plans'
  },

  // Access Permissions
  access_permissions: {
    feature: 'access_permissions',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Access permissions available on all plans'
  },

  // User Roles
  user_roles: {
    feature: 'user_roles',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'User roles coming soon to all plans'
  },

  // Create with AI
  create_with_ai: {
    feature: 'create_with_ai',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'AI creation available on all plans'
  },

  // Customize Meta Title/Description
  customize_meta: {
    feature: 'customize_meta',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Meta customization available on all plans'
  },

  // Google Indexing Controls
  google_indexing: {
    feature: 'google_indexing',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Google indexing controls available on all plans'
  },

  // Custom Fonts
  custom_fonts: {
    feature: 'custom_fonts',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Custom fonts available on all plans'
  },

  // Google Drive Integration
  google_drive_integration: {
    feature: 'google_drive_integration',
    allowedPlans: ['starter', 'pro', 'enterprise'],
    upgradeMessage: 'Google Drive integration available on all plans'
  },

  // Zapier Integration
  zapier_integration: {
    feature: 'zapier_integration',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for Zapier integration',
    upgradePlan: 'enterprise'
  },

  // Made in Journey Branding
  made_in_journey_branding: {
    feature: 'made_in_journey_branding',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan to remove branding',
    upgradePlan: 'pro'
  },

  // Workspaces
  workspaces: {
    feature: 'workspaces',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan for workspaces',
    upgradePlan: 'pro'
  },

  // Tags and Filters
  tags_and_filters: {
    feature: 'tags_and_filters',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan for tags and filters',
    upgradePlan: 'pro'
  },

  // AI Chatbot
  ai_chatbot: {
    feature: 'ai_chatbot',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan for AI chatbot',
    upgradePlan: 'pro'
  },

  // Customize NDA
  customize_nda: {
    feature: 'customize_nda',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for custom NDA',
    upgradePlan: 'enterprise'
  },

  // NDA Required
  nda_required: {
    feature: 'nda_required',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan to require NDAs',
    upgradePlan: 'pro'
  },

  // Organization Templates
  organization_templates: {
    feature: 'organization_templates',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for organization templates',
    upgradePlan: 'enterprise'
  },

  // Email Tracking
  email_tracking: {
    feature: 'email_tracking',
    allowedPlans: ['pro', 'enterprise'],
    upgradeMessage: 'Upgrade to Grow or Scale plan for email tracking',
    upgradePlan: 'pro'
  },

  // Clay Integration
  clay_integration: {
    feature: 'clay_integration',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for Clay integration',
    upgradePlan: 'enterprise'
  },

  // Other Integrations
  other_integrations: {
    feature: 'other_integrations',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Upgrade to Scale plan for additional integrations',
    upgradePlan: 'enterprise'
  },

  // Teams Plan Exclusive Features (Enterprise only)
  custom_team_pricing: {
    feature: 'custom_team_pricing',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  dedicated_account_manager: {
    feature: 'dedicated_account_manager',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  custom_integrations: {
    feature: 'custom_integrations',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  white_label_solutions: {
    feature: 'white_label_solutions',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  advanced_security_features: {
    feature: 'advanced_security_features',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  custom_training_onboarding: {
    feature: 'custom_training_onboarding',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  sla_guarantees: {
    feature: 'sla_guarantees',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  },
  priority_support: {
    feature: 'priority_support',
    allowedPlans: ['enterprise'],
    upgradeMessage: 'Contact us for access to this feature.'
  }
};

// Helper function to check if a feature is allowed for a given plan
export const isFeatureAllowed = (
  feature: string,
  planType: SubscriptionTypes | null,
  isOnTrial: boolean = false,
  hasReachedJourneyLimit: boolean = false
): boolean => {
  const restriction = FEATURE_RESTRICTIONS[feature];
  if (!restriction) {
    return true; // If no restriction defined, allow by default
  }

  // Special handling for journey_creation feature
  if (feature === 'journey_creation') {
    // Grow and Scale plans have unlimited journeys - no limits
    if (planType === 'pro' || planType === 'enterprise') {
      return true;
    }
    // For Start plan, check if user has reached their journey limit
    if (planType === 'starter' && hasReachedJourneyLimit) {
      return false;
    }
    // Otherwise, allow based on plan
    return planType ? restriction.allowedPlans.includes(planType) : false;
  }

  // Trial users get access to most features (similar to Scale plan)
  if (isOnTrial) {
    return true;
  }

  return planType ? restriction.allowedPlans.includes(planType) : false;
};

// Helper function to get upgrade message for a feature
export const getFeatureUpgradeMessage = (
  feature: string,
  planType: SubscriptionTypes | null
): string | null => {
  const restriction = FEATURE_RESTRICTIONS[feature];
  if (!restriction) {
    return null;
  }

  if (planType && restriction.allowedPlans.includes(planType)) {
    return null; // Feature is allowed for current plan
  }

  return restriction.upgradeMessage || 'Upgrade your plan to access this feature';
};

// Helper function to get the required plan for a feature
export const getFeatureRequiredPlan = (feature: string): SubscriptionTypes | null => {
  const restriction = FEATURE_RESTRICTIONS[feature];
  return restriction?.upgradePlan || null;
};

// Helper function to check if user needs to upgrade for a feature
export const needsUpgradeForFeature = (
  feature: string,
  planType: SubscriptionTypes | null,
  isOnTrial: boolean = false,
  hasReachedJourneyLimit: boolean = false
): boolean => {
  return !isFeatureAllowed(feature, planType, isOnTrial, hasReachedJourneyLimit);
};

// Feature groups for easier management
export const FEATURE_GROUPS = {
  ANALYTICS: ['basic_insights', 'advanced_insights', 'session_replay'],
  WORKSPACE: ['workspaces', 'organization_templates', 'tags_and_filters'],
  INTEGRATIONS: ['clay_integration', 'other_integrations', 'slack_notifications', 'google_drive_integration', 'zapier_integration'],
  CUSTOMIZATION: ['customize_nda', 'email_tracking', 'made_in_journey_branding', 'customize_meta', 'custom_fonts', 'google_indexing'],
  AI_FEATURES: ['ai_chatbot', 'create_with_ai'],
  JOURNEY_FEATURES: ['unlimited_journeys', 'journey_modes', 'content_blocks', 'content_library', 'synced_steps'],
  COLLABORATION: ['comments', 'access_permissions', 'user_roles', 'email_notifications']
};

// Helper function to check if any feature in a group is restricted
export const isFeatureGroupRestricted = (
  featureGroup: string[],
  planType: SubscriptionTypes | null,
  isOnTrial: boolean = false
): boolean => {
  return featureGroup.some(feature => 
    needsUpgradeForFeature(feature, planType, isOnTrial)
  );
}; 